import React, { useState, useRef, useEffect } from "react";
import { BsFillSendFill } from "react-icons/bs";
import { FaRegCircleStop } from "react-icons/fa6";
import ScenarioCards from "../components/ScenarioCards";
import ChatMessage from "../components/ChatMessage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Copilot = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Stream handler — parses Karini's chunked response format
  const processStream = async (response, questionText) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = "";
    let finalData = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      fullText += chunk;

      // Try to extract the streamed text (between #%&stream&%# and next separator or end)
      const streamMatch = fullText.match(/#%&stream&%#([\s\S]*?)(?=#%&|$)/);
      const streamText = streamMatch ? streamMatch[1] : "";

      // Filter out "Final#$%Answer>:" artifacts
      const cleanText = streamText.replace(/Final\s*#\$%Answer\s*(&gt;|>):/g, "");

      if (cleanText) {
        setMessages((prev) => [
          ...prev.slice(0, -1),
          {
            role: "assistant",
            content: cleanText,
            streaming: true,
          },
        ]);
      }

      // Try to extract the final response JSON
      const responseMatch = fullText.match(/#%&response&%#([\s\S]*?)$/);
      if (responseMatch) {
        try {
          finalData = JSON.parse(responseMatch[1]);
        } catch (e) {
          // Not complete yet, keep reading
        }
      }
    }

    // Finalize
    if (finalData) {
      if (finalData.thread_id) {
        setThreadId(finalData.thread_id);
      }
      const answer = finalData.response || fullText.replace(/#%&\w+&%#/g, "");
      setMessages((prev) => [
        ...prev.slice(0, -1),
        {
          role: "assistant",
          content: answer,
          streaming: false,
        },
      ]);
    } else {
      // Fallback: use accumulated stream text
      const cleanFull = fullText.replace(/#%&\w+&%#/g, "").replace(/Final\s*#\$%Answer\s*(&gt;|>):/g, "");
      setMessages((prev) => [
        ...prev.slice(0, -1),
        {
          role: "assistant",
          content: cleanFull || "No response received.",
          streaming: false,
        },
      ]);
    }
  };

  const sendMessage = async (text) => {
    const question = text || input.trim();
    if (!question) return;

    setInput("");
    setIsLoading(true);

    // Add user message + loading placeholder
    setMessages((prev) => [
      ...prev,
      { role: "user", content: question },
      { role: "assistant", content: "", streaming: true },
    ]);

    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question,
          thread_id: threadId,
        }),
      });

      await processStream(response, question);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [
        ...prev.slice(0, -1),
        {
          role: "assistant",
          content: "Something went wrong. Please try again.",
          streaming: false,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  const handleScenarioClick = (query) => {
    // Load question into input — don't send immediately
    setInput(query);
  };

  const handleNewChat = () => {
    setMessages([]);
    setThreadId(null);
  };

  return (
    <div className="h-full flex flex-col bg-slate-50">

      {/* Page Header */}
      <div className="px-6 py-4 bg-white border-b border-slate-200">
        <h2 className="text-xl font-bold text-slate-800">Category Manager Copilot</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          Proposes price, promotion, placement, inventory rebalancing, and replenishment actions based on daily signals across all dark stores.
        </p>
      </div>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-4xl mx-auto">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[50vh]">
              <h3 className="text-lg font-semibold text-emerald-700 mb-2">Quick Commerce Category Manager</h3>
              <p className="text-sm text-slate-600 text-center max-w-lg mb-8">
                Ask me a supply-chain question about pricing, promotions, placement, inventory rebalancing, or replenishment — I'll analyze the data and recommend actions in business terms.
              </p>
              <ScenarioCards onCardClick={handleScenarioClick} />
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <ChatMessage key={idx} message={msg} />
              ))}
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="border-t border-slate-200 bg-white px-4 py-4">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto flex items-center gap-3">
          <button
            type="button"
            onClick={handleNewChat}
            className="px-3 py-2.5 text-lg text-slate-400 hover:text-slate-600 transition"
            title="New chat"
          >
            +
          </button>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Enter your question here"
            disabled={isLoading}
            className="flex-1 px-4 py-3 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50 disabled:bg-slate-50"
          />
          {isLoading ? (
            <button
              type="button"
              className="w-10 h-10 flex items-center justify-center text-red-500 hover:text-red-600 transition"
            >
              <FaRegCircleStop size={18} />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!input.trim()}
              className="w-10 h-10 flex items-center justify-center text-emerald-500 hover:text-emerald-600 transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <BsFillSendFill size={18} />
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default Copilot;
